// XP for a logged cardio activity (a run, ride or swim without a library workout): its minutes times an
// effort factor from the heart rate zones or the felt effort (RPE). The same daily minutes and weekly
// sessions limits as every session apply on top (sessionXp in xp.ts). See docs/habit.md, "Cardio".
import { DEFAULT_MAX_HR, zoneOf, type CardioActivity } from '../cardio/activity';

export const CARDIO_XP = {
  /** XP per minute at a moderate effort (zone 2, or RPE 3 to 4). */
  xpPerMinute: 1,
  /** At most this for one activity, before the daily and weekly limits. */
  maxXp: 150,
  /** Effort factor per zone 1 to 5. */
  zoneFactor: [0.8, 1, 1.3, 1.6, 1.8] as const,
  /** Without heart rate or RPE. */
  defaultFactor: 1,
} as const;

/** Effort factor for an RPE of 1 to 10: easy 0.8, moderate 1, somewhat hard 1.3, hard 1.6, very hard 1.8. */
export function rpeFactor(rpe: number): number {
  const r = Math.min(10, Math.max(1, Math.round(rpe)));
  return CARDIO_XP.zoneFactor[Math.min(4, Math.floor((r - 1) / 2))]!;
}

/**
 * How hard an activity was, as a factor: from the time in zones when it covers at least half the activity,
 * else from the RPE, else from the average heart rate, else 1.
 */
export function effortFactor(a: Pick<CardioActivity, 'durationSec' | 'zoneSec' | 'rpe' | 'avgHr'>, maxHr = DEFAULT_MAX_HR): number {
  const zoneTotal = a.zoneSec?.reduce((s, z) => s + z, 0) ?? 0;
  if (a.zoneSec && zoneTotal > 0 && zoneTotal >= a.durationSec * 0.5) {
    return a.zoneSec.reduce((s, z, i) => s + z * CARDIO_XP.zoneFactor[i]!, 0) / zoneTotal;
  }
  if (a.rpe !== undefined) return rpeFactor(a.rpe);
  if (a.avgHr !== undefined) return CARDIO_XP.zoneFactor[zoneOf(a.avgHr, maxHr) - 1]!;
  return CARDIO_XP.defaultFactor;
}

/** The XP of an activity before the daily and weekly limits: minutes times XP per minute times effort, capped. */
export function cardioBaseXp(a: Pick<CardioActivity, 'durationSec' | 'zoneSec' | 'rpe' | 'avgHr'>): number {
  const minutes = Math.max(0, a.durationSec) / 60;
  return Math.min(CARDIO_XP.maxXp, minutes * CARDIO_XP.xpPerMinute * effortFactor(a));
}
